// src/services/googleBillingService.js
// Compra pelo Google Play Billing (só no app Android). Usado quando canalDePagamento() === 'google'.
//
// Fluxo: o plugin abre a tela de compra do Google → entrega o purchaseToken → mandamos ao servidor (validarCompraGoogle),
// que confirma com a API do Google e só então libera o plano/relatório. O app NUNCA libera nada sozinho.
// Desenho completo: docs/Billing_Google_Play_Desenho.md
import { NativePurchases, PURCHASE_TYPE } from '@capgo/native-purchases';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebase';

// plano do app → produto no Play Console (os IDs precisam ser iguais aos criados lá)
const PRODUTOS = {
  tactical: 'gestor_tactical',
  plus: 'gestor_plus',
  total: 'gestor_total',
  advisor: 'consultor_advisor',
  boutique: 'consultor_boutique',
  enterprise: 'consultor_enterprise',
  entry: 'entry',
  report_avulso: 'super360_avulso',
};
const PLANO_BASE_ASSINATURA = 'mensal'; // ID do plano-base mensal, igual em todas as assinaturas (criar assim no Console)

const validar = (dados) => httpsCallable(getFunctions(app, 'southamerica-east1'), 'validarCompraGoogle')(dados).then((r) => r.data);

export function produtoGoogleDoPlano(planId) {
  return PRODUTOS[planId] || null;
}

function foiCancelamentoDoUsuario(e) {
  const msg = String((e && (e.message || e.code)) || '').toLowerCase();
  return msg.includes('cancel') || msg.includes('user_cancel') || msg.includes('usercancel');
}

// Compra um plano ou relatório pelo Google. Devolve { ok: true } ou { cancelado: true }; lança Error com mensagem
// amigável nos demais casos.
export async function comprarPeloGoogle({ planId, uid, dossieId }) {
  const productIdentifier = produtoGoogleDoPlano(planId);
  if (!productIdentifier) throw new Error('Este item ainda não está disponível para compra pelo Google Play.');
  const assinatura = productIdentifier.startsWith('gestor_') || productIdentifier.startsWith('consultor_');

  let transacao;
  try {
    transacao = await NativePurchases.purchaseProduct({
      productIdentifier,
      productType: assinatura ? PURCHASE_TYPE.SUBS : PURCHASE_TYPE.INAPP,
      ...(assinatura ? { planIdentifier: PLANO_BASE_ASSINATURA } : {}),
      appAccountToken: uid, // o servidor confere que a compra é desta conta
      autoAcknowledgePurchases: false, // quem reconhece/consome é o servidor, DEPOIS de validar
    });
  } catch (e) {
    if (foiCancelamentoDoUsuario(e)) return { cancelado: true };
    console.error('[PHOENIX] Erro na compra pelo Google Play:', e);
    throw new Error('Não foi possível concluir a compra no Google Play. Tente novamente.');
  }

  const purchaseToken = transacao && transacao.purchaseToken;
  if (!purchaseToken) throw new Error('O Google Play não devolveu o código da compra. Tente novamente.');
  guardarCompraNaoConfirmada({ purchaseToken, productId: productIdentifier, dossieId: dossieId || null });

  try {
    await validar({ productId: productIdentifier, purchaseToken, dossieId: dossieId || null });
  } catch (e) {
    console.error('[PHOENIX] O servidor não confirmou a compra:', e);
    // A compra existe no Google; fica guardada para tentarmos confirmar de novo (recuperarComprasGoogle).
    throw new Error((e && e.message) || 'Pagamento recebido, mas ainda não conseguimos confirmar. Abra o app de novo em instantes.');
  }
  limparCompraNaoConfirmada(purchaseToken);
  return { ok: true };
}

// ---- Compras pagas mas ainda não confirmadas pelo servidor (app fechou, sem internet, etc.) ----
const CHAVE_PENDENTES = 'phoenix_compras_google_pendentes';
function lerPendentes() {
  try { return JSON.parse(localStorage.getItem(CHAVE_PENDENTES) || '[]'); } catch (e) { return []; }
}
function guardarCompraNaoConfirmada(item) {
  try {
    const lista = lerPendentes().filter((i) => i.purchaseToken !== item.purchaseToken);
    localStorage.setItem(CHAVE_PENDENTES, JSON.stringify([...lista, item]));
  } catch (e) { /* sem armazenamento: a compra continua recuperável pelo Google (restorePurchases) */ }
}
function limparCompraNaoConfirmada(token) {
  try { localStorage.setItem(CHAVE_PENDENTES, JSON.stringify(lerPendentes().filter((i) => i.purchaseToken !== token))); } catch (e) { /* ignora */ }
}

// Chamar quando o app abre com a pessoa logada: tenta confirmar no servidor as compras que ficaram pelo caminho.
export async function recuperarComprasGoogle() {
  const pendentes = lerPendentes();
  let confirmadas = 0;
  for (const item of pendentes) {
    try {
      await validar({ productId: item.productId, purchaseToken: item.purchaseToken, dossieId: item.dossieId });
      limparCompraNaoConfirmada(item.purchaseToken);
      confirmadas += 1;
    } catch (e) {
      console.warn('[PHOENIX] Compra do Google ainda não confirmada:', e && e.message);
    }
  }
  return { tentadas: pendentes.length, confirmadas };
}
